/* ============================================================
   dd-timeline.js — playhead pattern

   What this controls
   ------------------
   For each <section data-dd-timeline> on the page, it watches all
   .dd-timeline__panel children and decides which one the user is
   "currently reading" — i.e. which milestone is closest to the
   sticky playhead at viewport center. That panel gets [data-active];
   panels above it get [data-passed]; panels below stay clean.

   Why this is just IntersectionObserver and not scroll math
   ---------------------------------------------------------
   The playhead's visual job (stay pinned at viewport center while
   markers scroll past) is 100% CSS — `position: sticky; top: 50vh`
   inside the rail. There's no scroll-progress math to compute, no
   rAF loop to throttle. JS only has to answer one question: "which
   panel is currently sitting at the viewport's vertical center?"

   That's exactly what an IntersectionObserver with a 1px-tall
   horizontal slice at `top: 50%` is built to detect — entries fire
   whenever a panel's bounding box crosses that slice, and the most
   recent crossing tells us which panel owns the playhead.

   Active detection details
   ------------------------
   • Root margin `-50% 0px -49.99% 0px` collapses the observer
     viewport to a 0.01% horizontal slice at vertical center. Any
     panel intersecting that slice is the active one.
   • We use observer entries' `boundingClientRect.top` to also
     compute "passed" panels — those whose bottom has scrolled
     above the slice — so the rail markers behind the playhead
     read as "completed".
   • No scroll listener; IntersectionObserver hands us state
     transitions natively, which is far more efficient than
     polling getBoundingClientRect on every scroll frame.

   Reduced motion
   --------------
   We still run the observer (active highlighting is purely
   semantic state, not motion). What's skipped is the playhead's
   pulse animation — handled in CSS, not JS.
   ============================================================ */
(function () {
  'use strict';

  function DDTimeline(root) {
    this.root = root;
    this.panels = Array.prototype.slice.call(root.querySelectorAll('.dd-timeline__panel'));
    if (!this.panels.length) return;

    this.activeIdx = 0;
    this.observer = null;
    this.bind();
  }

  DDTimeline.prototype.bind = function () {
    if (!('IntersectionObserver' in window)) {
      /* Pre-IO browsers (rare in 2026, but still): leave the SSR
         `data-active` on the first panel and call it good. The
         layout works without active-highlighting; you just lose
         the dim/bright contrast on inactive panels. */
      return;
    }

    var self = this;
    this.observer = new IntersectionObserver(
      function (entries) {
        self.handleEntries(entries);
      },
      {
        /* Collapse the observer viewport to a hair-thin horizontal
           slice at vertical center. Any panel touching this slice
           is the active one.

           The `-49.99%` (rather than -50%) on the bottom keeps a
           ~0.01% sliver of root height so the observer is technically
           always non-degenerate — Safari has historically been
           glitchy with zero-area roots. */
        rootMargin: '-50% 0px -49.99% 0px',
        threshold: 0,
      }
    );

    this.panels.forEach(function (p) {
      self.observer.observe(p);
    });

    /* Initial pass: when JS boots mid-page (e.g. browser restored
       a scroll position), the observer's first batch of entries
       arrives async. Manually compute initial active state so the
       very first paint is correct rather than waiting for the next
       intersection event. */
    this.computeInitialState();
  };

  DDTimeline.prototype.handleEntries = function (entries) {
    var self = this;
    entries.forEach(function (entry) {
      if (entry.isIntersecting) {
        var idx = parseInt(entry.target.dataset.index, 10);
        if (!isNaN(idx)) self.setActive(idx);
      }
    });
  };

  /* Initial-state walk: pick the panel whose center is closest to
     viewport center. Used on boot and on resize (panels' anchors
     shift when their grid rows resize). */
  DDTimeline.prototype.computeInitialState = function () {
    var vhCenter = (window.innerHeight || document.documentElement.clientHeight) / 2;
    var best = 0;
    var bestDist = Infinity;

    this.panels.forEach(function (panel, i) {
      var rect = panel.getBoundingClientRect();
      var center = rect.top + rect.height / 2;
      var dist = Math.abs(center - vhCenter);
      if (dist < bestDist) {
        bestDist = dist;
        best = i;
      }
    });

    this.setActive(best);
  };

  DDTimeline.prototype.setActive = function (idx) {
    if (idx === this.activeIdx) {
      /* Even when activeIdx is stable, recompute passed-state below
         in case we got here via initial-state and some panels above
         haven't been flagged yet. */
    }
    this.activeIdx = idx;

    this.panels.forEach(function (panel, i) {
      if (i === idx) {
        panel.setAttribute('data-active', '');
        panel.removeAttribute('data-passed');
      } else if (i < idx) {
        panel.removeAttribute('data-active');
        panel.setAttribute('data-passed', '');
      } else {
        panel.removeAttribute('data-active');
        panel.removeAttribute('data-passed');
      }
    });
  };

  DDTimeline.prototype.destroy = function () {
    if (this.observer) {
      this.observer.disconnect();
      this.observer = null;
    }
  };

  function init() {
    var nodes = document.querySelectorAll('[data-dd-timeline]');
    Array.prototype.forEach.call(nodes, function (node) {
      if (node._ddTimeline) return;
      node._ddTimeline = new DDTimeline(node);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  /* Re-init when the theme editor re-renders the section so the
     observer is bound to the fresh DOM. */
  document.addEventListener('shopify:section:load', function (event) {
    var target = event.target;
    if (target && target.querySelector && target.querySelector('[data-dd-timeline]')) {
      init();
    }
  });

  document.addEventListener('shopify:section:unload', function (event) {
    var target = event.target;
    if (!target || !target.querySelector) return;
    var node = target.querySelector('[data-dd-timeline]');
    if (node && node._ddTimeline) {
      node._ddTimeline.destroy();
      node._ddTimeline = null;
    }
  });
})();
